import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { 
  Truck, 
  MapPin, 
  Clock, 
  Phone, 
  Bell, 
  Navigation,
  Scale,
  User,
  Timer,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Activity,
  Search,
  Filter,
  Settings,
  Shield,
  Camera
} from "lucide-react";
import type { Visit, WeighSession } from "../../../shared/schema";
import { CCTVMonitor } from "../components/cctv/cctv-monitor";
import { getAuthHeaders } from "../lib/api";

// CCTV Quick View Component
function CCTVQuickView() {
  return (
    <CCTVMonitor compact={true} />
  );
}

// Security Alerts Component  
function SecurityAlerts() {
  const alerts = [
    { id: 1, type: 'warning', camera: 'Exit Gate', message: 'Camera offline', time: '2 min ago' },
    { id: 2, type: 'info', camera: 'Scale Platform 1', message: 'Recording started', time: '5 min ago' }
  ];

  return (
    <Card className="bg-gradient-to-br from-red-50 to-white border-red-200 shadow-lg">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center text-sm">
          <Shield className="w-4 h-4 mr-2 text-red-600" />
          Security Alerts
          <Badge variant="destructive" className="ml-auto">
            {alerts.filter(a => a.type === 'warning').length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {alerts.map(alert => (
          <div key={alert.id} className={`p-3 rounded-lg border ${
            alert.type === 'warning' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'
          }`}>
            <div className="flex items-start space-x-2">
              {alert.type === 'warning' ? 
                <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" /> : 
                <Camera className="w-4 h-4 text-blue-500 mt-0.5" />
              }
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{alert.camera}</div>
                <div className="text-xs text-gray-600">{alert.message}</div>
                <div className="text-xs text-gray-400 mt-1">{alert.time}</div>
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

interface TruckWithDetails extends Visit {
  sessions?: WeighSession[];
  estimatedArrival?: string;
  currentLocation?: {
    lat: number;
    lng: number;
    address: string;
  };
}

function CompactTruckCard({ truck, onClick }: { truck: TruckWithDetails; onClick: () => void }) {
  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'expected': 
        return {
          card: 'border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-150 shadow-blue-100',
          dot: 'bg-gradient-to-r from-blue-400 to-blue-600 shadow-lg shadow-blue-200',
          accent: 'text-blue-700'
        };
      case 'processing': 
        return {
          card: 'border-orange-200 bg-gradient-to-br from-orange-50 to-orange-100 hover:from-orange-100 hover:to-orange-150 shadow-orange-100',
          dot: 'bg-gradient-to-r from-orange-400 to-orange-600 shadow-lg shadow-orange-200 animate-pulse',
          accent: 'text-orange-700'
        };
      case 'departed': 
        return {
          card: 'border-green-200 bg-gradient-to-br from-green-50 to-green-100 hover:from-green-100 hover:to-green-150 shadow-green-100',
          dot: 'bg-gradient-to-r from-green-400 to-green-600 shadow-lg shadow-green-200',
          accent: 'text-green-700'
        };
      default: 
        return {
          card: 'border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100',
          dot: 'bg-gray-500',
          accent: 'text-gray-700'
        };
    }
  };

  const style = getStatusStyle(truck.status);
  const hasWeight = truck.sessions && truck.sessions[0]?.netWeight;

  return (
    <div 
      className={`p-4 border rounded-xl cursor-pointer hover:shadow-lg hover:scale-105 transition-all duration-300 ${style.card} relative overflow-hidden group`}
      onClick={onClick} 
      data-testid={`truck-card-${truck.id}`}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,_rgba(120,120,120,0.3),transparent_50%)]"></div>
      </div>

      <div className="relative z-10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3 min-w-0 flex-1">
            <div className={`w-3 h-3 rounded-full ${style.dot}`}></div>
            <div className="min-w-0 flex-1">
              <div className="font-bold text-base truncate">{truck.plateNumber || truck.subjectExternalId}</div>
              <div className="text-xs text-muted-foreground truncate">{truck.driverName || 'Unknown Driver'}</div>
            </div>
          </div>
          
          <div className="flex items-center space-x-1">
            {hasWeight && (
              <div className="flex items-center text-xs font-medium bg-white/50 rounded-full px-2 py-1">
                <Scale className="w-3 h-3 mr-1" />
                <span>{Number(truck.sessions![0].netWeight).toLocaleString()}kg</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center space-x-1 text-muted-foreground">
            <Clock className="w-3 h-3" />
            <span>{truck.startedAt ? new Date(truck.startedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : truck.estimatedArrival || 'Pending'}</span>
          </div>
          
          {truck.status === 'processing' && (
            <div className="flex items-center space-x-1 animate-bounce">
              <Activity className="w-3 h-3 text-orange-600" />
              <span className={`text-xs font-medium ${style.accent}`}>LIVE</span>
            </div>
          )}
        </div>
      </div>

      {/* Hover Effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
    </div>
  );
}

function LiveViewDialog({ truck, open, onOpenChange, onCallDriver, onSendNotification }: { 
  truck: TruckWithDetails | null; 
  open: boolean; 
  onOpenChange: (open: boolean) => void;
  onCallDriver: () => void;
  onSendNotification: () => void;
}) {
  if (!truck) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center text-xl">
            <Activity className="w-6 h-6 mr-2 text-blue-500" />
            Live View - {truck.plateNumber || truck.subjectExternalId}
          </DialogTitle>
          <DialogDescription>
            Real-time tracking and monitoring for this truck
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Live Location Map */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <MapPin className="w-5 h-5 mr-2 text-green-500" />
                Live Location & Movement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="aspect-[16/10] bg-gradient-to-br from-blue-50 to-green-50 rounded-lg border-2 border-dashed border-blue-200 flex items-center justify-center">
                <div className="text-center space-y-4">
                  <div className="relative">
                    <Navigation className="w-16 h-16 mx-auto text-blue-500 animate-pulse" />
                    <div className="absolute -top-2 -right-2 w-4 h-4 bg-green-500 rounded-full animate-ping"></div>
                  </div>
                  <div>
                    <p className="text-lg font-medium text-blue-700">Live GPS Tracking Active</p>
                    <p className="text-sm text-muted-foreground">
                      Current: {truck.currentLocation?.address || 'Approaching weighbridge entrance'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Speed: 15 km/h • Direction: Northeast • Signal: Strong
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Live Vehicle Data */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Truck className="w-5 h-5 mr-2 text-orange-500" />
                  Vehicle Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">License Plate</span>
                    <span className="font-mono font-bold">{truck.plateNumber || 'Not detected'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Driver</span>
                    <span>{truck.driverName || 'Pending identification'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Current Status</span>
                    <Badge className={truck.status === 'open' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}>
                      {truck.status === 'open' ? 'On Site' : truck.status}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Access Decision</span>
                    <Badge variant={truck.accessDecision === 'PASS' ? 'default' : 'secondary'}>
                      {truck.accessDecision || 'Processing...'}
                    </Badge>
                  </div>
                </div>

                <div className="pt-4 space-y-2">
                  <Button 
                    onClick={onCallDriver} 
                    className="w-full flex items-center justify-center" 
                    variant="outline"
                    data-testid="live-call-driver"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Call Driver
                  </Button>
                  <Button 
                    onClick={onSendNotification}
                    className="w-full flex items-center justify-center"
                    data-testid="live-send-alert"
                  >
                    <Bell className="w-4 h-4 mr-2" />
                    Send Alert
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Live Weighing Data */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Scale className="w-5 h-5 mr-2 text-purple-500" />
                  Live Weighing
                </CardTitle>
              </CardHeader>
              <CardContent>
                {truck.sessions && truck.sessions.length > 0 ? (
                  <div className="space-y-4">
                    {truck.sessions.map((session) => (
                      <div key={session.id} className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">Session #{session.id}</span>
                          <Badge variant="outline" className="animate-pulse">
                            {session.status === 'open' ? 'LIVE' : session.status}
                          </Badge>
                        </div>
                        
                        <div className="bg-muted/30 p-4 rounded-lg space-y-3">
                          <div className="grid grid-cols-1 gap-3">
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">Gross Weight</span>
                              <span className="font-mono font-bold">
                                {session.grossWeight ? `${Number(session.grossWeight).toLocaleString()} kg` : 'Measuring...'}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">Tare Weight</span>
                              <span className="font-mono font-bold">
                                {session.tareWeight ? `${Number(session.tareWeight).toLocaleString()} kg` : 'Pending'}
                              </span>
                            </div>
                            <div className="flex justify-between border-t pt-2">
                              <span className="text-sm font-medium">Net Weight</span>
                              <span className="font-mono font-bold text-green-600">
                                {session.netWeight ? `${Number(session.netWeight).toLocaleString()} kg` : 'Calculating...'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Scale className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                    <p className="text-sm text-muted-foreground">No active weighing session</p>
                    <p className="text-xs text-muted-foreground">Waiting for truck to enter scale</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Live Activity Feed */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Activity className="w-5 h-5 mr-2 text-blue-500" />
                Live Activity Feed
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                <div className="flex items-center text-sm">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3 animate-pulse"></div>
                  <span className="text-muted-foreground mr-2">
                    {new Date().toLocaleTimeString()}
                  </span>
                  <span>GPS signal strong, approaching entrance</span>
                </div>
                <div className="flex items-center text-sm">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  <span className="text-muted-foreground mr-2">
                    {new Date(Date.now() - 30000).toLocaleTimeString()}
                  </span>
                  <span>License plate detected: {truck.plateNumber}</span>
                </div>
                <div className="flex items-center text-sm">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></div>
                  <span className="text-muted-foreground mr-2">
                    {new Date(Date.now() - 60000).toLocaleTimeString()}
                  </span>
                  <span>Access control verification in progress</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TruckGrid({ 
  trucks, 
  searchTerm, 
  emptyIcon: EmptyIcon, 
  emptyText,
  onTruckClick 
}: { 
  trucks: TruckWithDetails[]; 
  searchTerm: string;
  emptyIcon: any;
  emptyText: string;
  onTruckClick: (truck: TruckWithDetails) => void;
}) {
  // Filter trucks based on search term
  const filteredTrucks = trucks.filter(truck => 
    !searchTerm || 
    truck.plateNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    truck.driverName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    truck.subjectExternalId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (filteredTrucks.length === 0) {
    return (
      <div className="flex items-center justify-center h-96 text-muted-foreground">
        {searchTerm ? (
          <div className="text-center">
            <Search className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-lg font-medium mb-2">No trucks found</p>
            <p className="text-sm">Try adjusting your search terms</p>
          </div>
        ) : (
          <div className="text-center">
            <EmptyIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-lg font-medium mb-2">{emptyText}</p>
            <p className="text-sm">Trucks will appear here as they arrive</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {searchTerm && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {filteredTrucks.length} of {trucks.length} trucks
          </p>
          <Badge variant="outline">{filteredTrucks.length} results</Badge>
        </div>
      )}
      
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
        {filteredTrucks.map((truck) => (
          <CompactTruckCard 
            key={truck.id} 
            truck={truck} 
            onClick={() => onTruckClick(truck)}
          />
        ))}
      </div>
    </div>
  );
}

function TimelineView({ trucks, searchTerm, onTruckClick }: {
  trucks: TruckWithDetails[];
  searchTerm: string;
  onTruckClick: (truck: TruckWithDetails) => void;
}) {
  const [weeklyCapacity, setWeeklyCapacity] = useState(700); // Default: 700 trucks per week
  // Generate time slots (6 AM to 10 PM in 1-hour increments)
  const timeSlots = Array.from({ length: 17 }, (_, i) => {
    const hour = i + 6;
    return {
      time: hour,
      label: hour < 12 ? `${hour}:00 AM` : hour === 12 ? '12:00 PM' : `${hour - 12}:00 PM`
    };
  });

  // Generate days of the week starting from today
  const days = Array.from({ length: 7 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() + i);
    return {
      date,
      label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : date.toLocaleDateString('en', { weekday: 'short' }),
      fullLabel: date.toLocaleDateString('en', { month: 'short', day: 'numeric' })
    };
  });

  // Filter trucks based on search term
  const filteredTrucks = trucks.filter(truck => 
    !searchTerm || 
    truck.plateNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    truck.driverName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    truck.subjectExternalId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get current time for timeline indicator
  const currentHour = new Date().getHours();
  const currentDay = new Date().getDay();

  // Group trucks by day and time
  const getTrucksForSlot = (dayIndex: number, hour: number) => {
    return filteredTrucks.filter(truck => {
      if (truck.estimatedArrival) {
        // Simple time parsing - in real app would be more robust
        const estimatedHour = parseInt(truck.estimatedArrival.split(':')[0]);
        return estimatedHour === hour;
      }
      // For demo, distribute trucks across different time slots
      return (truck.id % 24 + 6) === hour && (truck.id % 7) === dayIndex;
    });
  };

  // Get total trucks for a specific day
  const getTrucksForDay = (dayIndex: number) => {
    return filteredTrucks.filter(truck => {
      // For demo, distribute trucks across different days
      return (truck.id % 7) === dayIndex;
    });
  };

  // Get total trucks for the week
  const getTrucksForWeek = () => {
    return filteredTrucks;
  };

  return (
    <Card className="bg-gradient-to-br from-purple-50/50 to-white border-purple-200 shadow-xl">
      <CardHeader className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
        <CardTitle className="flex items-center">
          <div className="p-2 bg-white/20 rounded-lg mr-3">
            <Timer className="w-6 h-6" />
          </div>
          <div>
            <div className="text-xl font-bold">Operations Timeline</div>
            <div className="text-purple-100 text-sm">Scheduled truck arrivals and processing times</div>
          </div>
          <div className="ml-auto flex items-center space-x-3">
            <div className="bg-white/20 rounded-lg p-2">
              <Label htmlFor="capacity" className="text-purple-100 text-xs">Weekly Capacity</Label>
              <Select value={weeklyCapacity.toString()} onValueChange={(value) => setWeeklyCapacity(Number(value))}>
                <SelectTrigger className="w-24 h-8 bg-white text-purple-600 border-none text-xs font-bold">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="350">350</SelectItem>
                  <SelectItem value="500">500</SelectItem>
                  <SelectItem value="700">700</SelectItem>
                  <SelectItem value="1000">1000</SelectItem>
                  <SelectItem value="1400">1400</SelectItem>
                  <SelectItem value="2000">2000</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Badge variant="secondary" className="bg-white text-purple-600 font-bold">
              {filteredTrucks.length}/{weeklyCapacity} weekly
            </Badge>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-auto max-h-[70vh]">
          <div className="grid grid-cols-8 min-w-[1000px]">
            {/* Header Row - Days */}
            <div className="sticky top-0 bg-gray-50 border-b border-gray-200 p-3">
              <div className="text-sm font-medium text-gray-500">Time</div>
            </div>
            {days.map((day, dayIndex) => {
              const dayTrucks = getTrucksForDay(dayIndex);
              const weekTrucks = getTrucksForWeek();
              const isOverWeeklyCapacity = weekTrucks.length > weeklyCapacity;
              const dailyAverage = Math.round(weeklyCapacity / 7);
              const isOverDailyAverage = dayTrucks.length > dailyAverage;
              
              return (
                <div key={dayIndex} className={`sticky top-0 border-b border-l border-gray-200 p-3 text-center ${
                  isOverWeeklyCapacity ? 'bg-red-100 border-red-300' : 
                  isOverDailyAverage ? 'bg-yellow-100 border-yellow-300' : 'bg-gray-50'
                }`}>
                  <div className="font-semibold text-gray-900">{day.label}</div>
                  <div className="text-xs text-gray-500">{day.fullLabel}</div>
                  <div className={`text-xs font-medium mt-1 ${
                    isOverWeeklyCapacity ? 'text-red-600' : 
                    isOverDailyAverage ? 'text-yellow-600' : 'text-gray-600'
                  }`}>
                    {dayTrucks.length}/{dailyAverage}
                    {isOverWeeklyCapacity && <AlertTriangle className="w-3 h-3 inline ml-1 text-red-500" />}
                    {isOverDailyAverage && !isOverWeeklyCapacity && <AlertTriangle className="w-3 h-3 inline ml-1 text-yellow-500" />}
                  </div>
                </div>
              );
            })}

            {/* Time Slots */}
            {timeSlots.map(({ time, label }) => (
              <div key={time} className="contents">
                {/* Time Label */}
                <div className={`border-b border-gray-200 p-3 bg-gray-50 sticky left-0 z-10 ${
                  time === currentHour ? 'bg-purple-100 border-purple-300' : ''
                }`}>
                  <div className="text-sm font-medium text-gray-700">{label}</div>
                </div>

                {/* Day Columns */}
                {days.map((day, dayIndex) => {
                  const trucksInSlot = getTrucksForSlot(dayIndex, time);
                  const isCurrentTime = time === currentHour && dayIndex === 0;
                  const weekTrucks = getTrucksForWeek();
                  const isOverWeeklyCapacity = weekTrucks.length > weeklyCapacity;
                  const dayTrucks = getTrucksForDay(dayIndex);
                  const dailyAverage = Math.round(weeklyCapacity / 7);
                  const isOverDailyAverage = dayTrucks.length > dailyAverage;
                  
                  return (
                    <div key={`${time}-${dayIndex}`} className={`border-b border-l border-gray-200 p-2 min-h-[80px] relative ${
                      isCurrentTime ? 'bg-purple-50 border-purple-200' : 
                      isOverWeeklyCapacity ? 'bg-red-50/30 border-red-200' :
                      isOverDailyAverage ? 'bg-yellow-50/30 border-yellow-200' :
                      'bg-white hover:bg-gray-50'
                    }`}>
                      {isCurrentTime && (
                        <div className="absolute top-0 left-0 right-0 h-1 bg-purple-500 rounded-full animate-pulse"></div>
                      )}
                      
                      <div className="space-y-1">
                        {trucksInSlot.map((truck) => (
                          <div
                            key={truck.id}
                            onClick={() => onTruckClick(truck)}
                            className={`p-2 rounded-md cursor-pointer hover:shadow-md transition-all text-xs ${
                              truck.status === 'expected' ? 'bg-blue-100 border border-blue-200 hover:bg-blue-200' :
                              truck.status === 'processing' ? 'bg-orange-100 border border-orange-200 hover:bg-orange-200 animate-pulse' :
                              'bg-green-100 border border-green-200 hover:bg-green-200'
                            }`}
                            data-testid={`timeline-truck-${truck.id}`}
                          >
                            <div className="font-medium truncate">{truck.plateNumber || truck.subjectExternalId}</div>
                            <div className="text-xs text-gray-600 truncate">{truck.driverName || 'Unknown'}</div>
                            {truck.sessions?.[0]?.netWeight && (
                              <div className="text-xs font-medium text-gray-700">
                                {Number(truck.sessions[0].netWeight).toLocaleString()}kg
                              </div>
                            )}
                          </div>
                        ))}
                        
                        {trucksInSlot.length === 0 && (
                          <div className="text-center text-gray-400 py-4 text-xs">
                            Available
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Operations() {
  const [selectedTruck, setSelectedTruck] = useState<TruckWithDetails | null>(null);
  const [liveViewOpen, setLiveViewOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch all visits and sessions
  const { data: visits = [], isLoading: visitsLoading } = useQuery<Visit[]>({
    queryKey: ['/api/planning/visits'],
  });

  const { data: sessions = [] } = useQuery<WeighSession[]>({
    queryKey: ['/api/sessions'],
  });

  // Transform data for operational view
  const trucksWithDetails: TruckWithDetails[] = visits.map(visit => ({
    ...visit,
    sessions: sessions.filter(session => session.visitId === visit.id),
    estimatedArrival: visit.startedAt ? undefined : "15:30", // Mock ETA for expected trucks
    currentLocation: visit.status === 'open' ? {
      lat: -26.2041,
      lng: 28.0473,
      address: "Approaching Gate 2"
    } : undefined,
  }));

  // Categorize trucks by status
  const expectedTrucks = trucksWithDetails.filter(truck => 
    !truck.startedAt || truck.status === 'scheduled'
  );
  
  const processingTrucks = trucksWithDetails.filter(truck => 
    truck.startedAt && !truck.endedAt && truck.status === 'open'
  );
  
  const departedTrucks = trucksWithDetails.filter(truck => 
    truck.endedAt || truck.status === 'closed'
  );

  const handleTruckClick = (truck: TruckWithDetails) => {
    setSelectedTruck(truck);
    setLiveViewOpen(true);
  };

  const handleCallDriver = async () => {
    if (selectedTruck) {
      try {
        const response = await fetch('/api/operations/call-driver', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ 
            visitId: selectedTruck.id, 
            driverPhone: '+1234567890' // In real implementation, get from driver data
          })
        });
        
        if (response.ok) {
          console.log(`Call initiated for truck ${selectedTruck.plateNumber}`);
        }
      } catch (error) {
        console.error('Failed to initiate call:', error);
      }
    }
  };

  const handleSendNotification = async () => {
    if (selectedTruck) {
      try {
        const response = await fetch('/api/operations/send-notification', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ 
            visitId: selectedTruck.id, 
            message: `Update for ${selectedTruck.plateNumber}: Please proceed to assigned lane`,
            type: 'lane_assignment'
          })
        });
        
        if (response.ok) {
          console.log(`Notification sent for truck ${selectedTruck.plateNumber}`);
        }
      } catch (error) {
        console.error('Failed to send notification:', error);
      }
    }
  };

  if (visitsLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Operations Dashboard</h1>
            <p className="text-muted-foreground">Real-time weighbridge operations and truck tracking</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <div className="animate-pulse">
                  <div className="h-6 bg-muted rounded w-3/4"></div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="animate-pulse space-y-3">
                  <div className="h-4 bg-muted rounded"></div>
                  <div className="h-4 bg-muted rounded w-2/3"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
          {/* Header with Search */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Operations Control</h1>
              <p className="text-muted-foreground">
                Live monitoring • {expectedTrucks.length + processingTrucks.length + departedTrucks.length} trucks total
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Global Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search trucks, plates, drivers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-80"
                  data-testid="search-trucks"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-1 px-3 py-1 bg-green-50 border border-green-200 rounded-full">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-green-700">LIVE</span>
                </div>
                <Button variant="outline" className="flex items-center" data-testid="refresh-operations">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </div>
          </div>

          {/* View Toggle */}
          <Tabs defaultValue="timeline" className="w-full">
            <TabsList className="grid w-full grid-cols-4 h-16 p-1 bg-gradient-to-r from-slate-100 to-slate-50 border shadow-lg">
              <TabsTrigger 
                value="timeline" 
                className="flex items-center space-x-2 h-full rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-lg hover:bg-purple-50 transition-all duration-300" 
                data-testid="timeline-tab"
              >
                <div className="flex items-center space-x-2">
                  <Timer className="w-5 h-5" />
                  <div>
                    <div className="font-semibold">Timeline</div>
                    <div className="text-xs opacity-80">Schedule View</div>
                  </div>
                </div>
              </TabsTrigger>
              <TabsTrigger 
                value="expected" 
                className="flex items-center space-x-2 h-full rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg hover:bg-blue-50 transition-all duration-300" 
                data-testid="expected-tab"
              >
                <div className="flex items-center space-x-2">
                  <Clock className="w-5 h-5" />
                  <div>
                    <div className="font-semibold">Expected</div>
                    <div className="text-xs opacity-80">{expectedTrucks.length} trucks</div>
                  </div>
                </div>
              </TabsTrigger>
              <TabsTrigger 
                value="processing" 
                className="flex items-center space-x-2 h-full rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-orange-600 data-[state=active]:text-white data-[state=active]:shadow-lg hover:bg-orange-50 transition-all duration-300"
                data-testid="processing-tab"
              >
                <div className="flex items-center space-x-2">
                  <RefreshCw className="w-5 h-5" />
                  <div>
                    <div className="font-semibold">Processing</div>
                    <div className="text-xs opacity-80">{processingTrucks.length} active</div>
                  </div>
                </div>
              </TabsTrigger>
              <TabsTrigger 
                value="departed" 
                className="flex items-center space-x-2 h-full rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-green-600 data-[state=active]:text-white data-[state=active]:shadow-lg hover:bg-green-50 transition-all duration-300"
                data-testid="departed-tab"
              >
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5" />
                  <div>
                    <div className="font-semibold">Departed</div>
                    <div className="text-xs opacity-80">{departedTrucks.length} completed</div>
                  </div>
                </div>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="timeline" className="mt-6">
              <TimelineView 
                trucks={trucksWithDetails}
                searchTerm={searchTerm}
                onTruckClick={handleTruckClick}
              />
            </TabsContent>

            <TabsContent value="expected" className="mt-6">
              <Card className="bg-gradient-to-br from-blue-50/50 to-white border-blue-200 shadow-xl">
                <CardHeader className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                  <CardTitle className="flex items-center">
                    <div className="p-2 bg-white/20 rounded-lg mr-3">
                      <Clock className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="text-xl font-bold">Expected Trucks</div>
                      <div className="text-blue-100 text-sm">Incoming vehicles awaiting processing</div>
                    </div>
                    <Badge variant="secondary" className="ml-auto bg-white text-blue-600 font-bold">
                      {expectedTrucks.length} trucks
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <TruckGrid
                    trucks={expectedTrucks}
                    searchTerm={searchTerm}
                    emptyIcon={Clock}
                    emptyText="No expected trucks"
                    onTruckClick={handleTruckClick}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="processing" className="mt-6">
              <Card className="bg-gradient-to-br from-orange-50/50 to-white border-orange-200 shadow-xl">
                <CardHeader className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                  <CardTitle className="flex items-center">
                    <div className="p-2 bg-white/20 rounded-lg mr-3">
                      <RefreshCw className="w-6 h-6 animate-spin" />
                    </div>
                    <div>
                      <div className="text-xl font-bold">Processing Trucks</div>
                      <div className="text-orange-100 text-sm">Currently being weighed and processed</div>
                    </div>
                    <Badge variant="secondary" className="ml-auto bg-white text-orange-600 font-bold animate-pulse">
                      {processingTrucks.length} active
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <TruckGrid
                    trucks={processingTrucks}
                    searchTerm={searchTerm}
                    emptyIcon={RefreshCw}
                    emptyText="No trucks processing"
                    onTruckClick={handleTruckClick}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="departed" className="mt-6">
              <Card className="bg-gradient-to-br from-green-50/50 to-white border-green-200 shadow-xl">
                <CardHeader className="bg-gradient-to-r from-green-500 to-green-600 text-white">
                  <CardTitle className="flex items-center">
                    <div className="p-2 bg-white/20 rounded-lg mr-3">
                      <CheckCircle className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="text-xl font-bold">Departed Trucks</div>
                      <div className="text-green-100 text-sm">Successfully completed and departed</div>
                    </div>
                    <Badge variant="secondary" className="ml-auto bg-white text-green-600 font-bold">
                      {departedTrucks.length} completed
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <TruckGrid
                    trucks={departedTrucks}
                    searchTerm={searchTerm}
                    emptyIcon={CheckCircle}
                    emptyText="No departed trucks"
                    onTruckClick={handleTruckClick}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* CCTV Integration */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <CCTVQuickView />
            </div>
            <SecurityAlerts />
          </div>

          {/* Live View Dialog */}
          <LiveViewDialog 
            truck={selectedTruck}
            open={liveViewOpen}
            onOpenChange={setLiveViewOpen}
            onCallDriver={handleCallDriver}
            onSendNotification={handleSendNotification}
          />
    </div>
  );
}